import React, { useState, useEffect, useMemo } from 'react';
import { listenToOrders, listenToRiders } from '../services/firebase';
import DateFilterBar from '../components/DateFilterBar';
import { todayKey, formatDayLabel } from '../utils/dates';
import { riderDailyCollection, isCod, orderTotal } from '../utils/orders';

const fmtTime = (ts) => {
  const d = ts?.toDate?.();
  if (!d) return '—';
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

const fmtMoney = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;

export default function RiderCollections() {
  const [orders, setOrders] = useState([]);
  const [riders, setRiders] = useState([]);
  const [selectedDay, setSelectedDay] = useState(todayKey());
  const [selectedRiderId, setSelectedRiderId] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const unsubOrders = listenToOrders(setOrders);
    const unsubRiders = listenToRiders(setRiders);
    return () => { unsubOrders(); unsubRiders(); };
  }, []);

  const collectionMap = useMemo(
    () => riderDailyCollection(orders, selectedDay),
    [orders, selectedDay],
  );

  const riderById = useMemo(() => {
    const map = {};
    riders.forEach((r) => { map[r.id] = r; });
    return map;
  }, [riders]);

  const rows = useMemo(() => {
    const ids = new Set([
      ...riders.map((r) => r.id),
      ...Object.keys(collectionMap),
    ]);
    return [...ids]
      .map((id) => {
        const rider = riderById[id];
        const col = collectionMap[id];
        const name = rider?.name || col?.name || 'Unknown';
        const phone = rider?.phone || '—';
        return {
          id,
          name,
          phone,
          deliveries: col?.deliveries ?? 0,
          codTotal: col?.cod.total ?? 0,
          codCount: col?.cod.count ?? 0,
          onlineTotal: col?.online.total ?? 0,
          onlineCount: col?.online.count ?? 0,
          orders: col?.orders ?? [],
        };
      })
      .filter((row) => {
        if (!search) return row.deliveries > 0;
        const q = search.toLowerCase();
        return row.deliveries > 0 && (
          row.name.toLowerCase().includes(q)
          || row.phone.includes(q)
          || row.id.toLowerCase().includes(q)
        );
      })
      .sort((a, b) => b.codTotal - a.codTotal || b.deliveries - a.deliveries);
  }, [riders, collectionMap, riderById, search]);

  const totals = useMemo(() => rows.reduce((acc, r) => {
    acc.deliveries += r.deliveries;
    acc.codTotal += r.codTotal;
    acc.codCount += r.codCount;
    acc.onlineTotal += r.onlineTotal;
    acc.onlineCount += r.onlineCount;
    return acc;
  }, { deliveries: 0, codTotal: 0, codCount: 0, onlineTotal: 0, onlineCount: 0 }), [rows]);

  const selected = selectedRiderId
    ? rows.find((r) => r.id === selectedRiderId)
    : null;

  return (
    <div className="page-body">
      <div style={{ marginBottom: 24, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16 }}>
        <div>
          <div style={{ fontFamily: 'Clash Display, sans-serif', fontSize: 24, fontWeight: 700, marginBottom: 4 }}>
            Rider Collections
          </div>
          <div style={{ fontSize: 13, color: 'var(--text2)' }}>
            Cash to collect from riders · {formatDayLabel(selectedDay)}
          </div>
        </div>
        <DateFilterBar selectedDay={selectedDay} onChange={setSelectedDay} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
        <div className="stat-card" style={{ borderColor: 'rgba(255,193,7,0.35)' }}>
          <div className="stat-label">COD to collect</div>
          <div className="stat-value" style={{ color: '#ffc107' }}>{fmtMoney(totals.codTotal)}</div>
          <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 4 }}>{totals.codCount} cash orders</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Online (prepaid)</div>
          <div className="stat-value" style={{ color: 'var(--blue)' }}>{fmtMoney(totals.onlineTotal)}</div>
          <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 4 }}>{totals.onlineCount} orders · no cash due</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Deliveries</div>
          <div className="stat-value">{totals.deliveries}</div>
          <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 4 }}>{rows.length} rider{rows.length !== 1 ? 's' : ''} active</div>
        </div>
      </div>

      <div style={{ padding: '12px 14px', marginBottom: 16, background: 'rgba(255,193,7,0.06)', border: '1px solid rgba(255,193,7,0.2)', borderRadius: 10, fontSize: 13, color: 'var(--text2)', lineHeight: 1.5 }}>
        <strong style={{ color: '#ffc107' }}>Collect from rider</strong> = full order total for COD deliveries that day.
        Online orders were paid in the app — riders do not owe that cash.
      </div>

      <div style={{ position: 'relative', maxWidth: 320, marginBottom: 16 }}>
        <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 14 }}>🔍</span>
        <input
          placeholder="Search rider name or phone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ paddingLeft: 32, width: '100%' }}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 360px' : '1fr', gap: 20 }}>
        <div className="table-card">
          <table>
            <thead>
              <tr>
                <th>RIDER</th>
                <th>PHONE</th>
                <th>DELIVERIES</th>
                <th>COD</th>
                <th>ONLINE</th>
                <th style={{ color: '#ffc107' }}>COLLECT</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', color: 'var(--text2)', padding: 32 }}>
                    No delivered orders for this day
                  </td>
                </tr>
              ) : (
                <>
                  {rows.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => setSelectedRiderId(row.id)}
                      style={{
                        cursor: 'pointer',
                        background: selectedRiderId === row.id ? 'rgba(255,193,7,0.06)' : '',
                      }}
                    >
                      <td>
                        <div style={{ fontWeight: 600 }}>{row.name}</div>
                        <div style={{ fontSize: 10, color: 'var(--text2)', fontFamily: 'JetBrains Mono' }}>{row.codCount} COD · {row.onlineCount} online</div>
                      </td>
                      <td style={{ fontFamily: 'JetBrains Mono', fontSize: 12 }}>{row.phone}</td>
                      <td style={{ fontFamily: 'JetBrains Mono' }}>{row.deliveries}</td>
                      <td style={{ fontFamily: 'JetBrains Mono', color: '#ffc107' }}>{fmtMoney(row.codTotal)}</td>
                      <td style={{ fontFamily: 'JetBrains Mono', color: 'var(--blue)' }}>{fmtMoney(row.onlineTotal)}</td>
                      <td style={{ fontFamily: 'JetBrains Mono', fontWeight: 700, color: '#ffc107' }}>{fmtMoney(row.codTotal)}</td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: '2px solid var(--border2)', fontWeight: 700 }}>
                    <td colSpan={2}>Total</td>
                    <td style={{ fontFamily: 'JetBrains Mono' }}>{totals.deliveries}</td>
                    <td style={{ fontFamily: 'JetBrains Mono', color: '#ffc107' }}>{fmtMoney(totals.codTotal)}</td>
                    <td style={{ fontFamily: 'JetBrains Mono', color: 'var(--blue)' }}>{fmtMoney(totals.onlineTotal)}</td>
                    <td style={{ fontFamily: 'JetBrains Mono', color: '#ffc107' }}>{fmtMoney(totals.codTotal)}</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>

        {selected && (
          <div className="table-card" style={{ padding: 20, height: 'fit-content', maxHeight: '70vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <div style={{ fontFamily: 'Clash Display, sans-serif', fontSize: 16, fontWeight: 600 }}>{selected.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text2)', fontFamily: 'JetBrains Mono' }}>{selected.phone}</div>
              </div>
              <button type="button" onClick={() => setSelectedRiderId(null)} style={{ background: 'none', border: 'none', color: 'var(--text2)', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
              <div style={{ padding: 12, background: 'rgba(255,193,7,0.08)', borderRadius: 8, border: '1px solid rgba(255,193,7,0.2)' }}>
                <div style={{ fontSize: 10, color: 'var(--text2)', fontFamily: 'JetBrains Mono' }}>COLLECT</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#ffc107' }}>{fmtMoney(selected.codTotal)}</div>
              </div>
              <div style={{ padding: 12, background: 'var(--surface)', borderRadius: 8 }}>
                <div style={{ fontSize: 10, color: 'var(--text2)', fontFamily: 'JetBrains Mono' }}>ONLINE</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--blue)' }}>{fmtMoney(selected.onlineTotal)}</div>
              </div>
            </div>

            <div style={{ fontSize: 11, color: 'var(--text2)', fontFamily: 'JetBrains Mono', marginBottom: 10, letterSpacing: 0.5 }}>
              ORDERS ({selected.orders.length})
            </div>
            {selected.orders.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--text2)' }}>No orders</div>
            ) : selected.orders.map((o) => (
              <div
                key={o.id}
                style={{
                  padding: 10,
                  marginBottom: 8,
                  background: 'var(--surface)',
                  borderRadius: 8,
                  borderLeft: `3px solid ${isCod(o) ? '#ffc107' : 'var(--blue)'}`,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{o.shopName || 'Shop'}</div>
                    <div style={{ fontSize: 10, color: 'var(--text2)', fontFamily: 'JetBrains Mono' }}>
                      #{o.id.slice(0, 8)} · {fmtTime(o.deliveredAt || o.updatedAt)}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: 'JetBrains Mono', fontWeight: 700, color: isCod(o) ? '#ffc107' : 'var(--blue)' }}>
                      {fmtMoney(orderTotal(o))}
                    </div>
                    <span className={`badge ${isCod(o) ? 'badge-orange' : 'badge-blue'}`} style={{ fontSize: 10 }}>
                      {isCod(o) ? 'COD' : 'Online'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
